import { Link } from 'react-router-dom';

const heroImage = 'about dlvry.PNG';
const teamImage = 'about w.PNG';

// Dehleze brand orange, expressed as tints/shades of the same hue
const BRAND = '#fd9e10';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fff8ee] via-[#fffaf3] to-white flex flex-col relative overflow-hidden">
      {/* Ambient brand-orange blobs */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-gradient-to-br from-[#fd9e10]/30 via-[#ffb74d]/20 to-transparent blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -right-40 w-[32rem] h-[32rem] rounded-full bg-gradient-to-bl from-[#fd9e10]/25 via-[#ffd08a]/20 to-transparent blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/4 w-[24rem] h-[24rem] rounded-full bg-gradient-to-tr from-[#ffe3b3]/40 to-transparent blur-3xl" />

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-24 relative z-10">
        {/* Breadcrumb */}
        <nav className="text-xs tracking-wide text-gray-500">
          <Link to="/" className="hover:text-gray-800 transition-colors">
            Home
          </Link>{' '}
          <span className="mx-1 text-gray-300">/</span>{' '}
          <span
            className="font-semibold bg-clip-text text-transparent"
            style={{ backgroundImage: `linear-gradient(to right, ${BRAND}, #e8720c)` }}
          >
            About Us
          </span>
        </nav>

        {/* Hero */}
        <section className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <span
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-xs font-semibold tracking-wide border shadow-sm"
              style={{ color: '#c9660a', borderColor: '#ffd9a0' }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: BRAND }}
              />
              ABOUT DEHLEZE
            </span>

            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold leading-[1.05] tracking-tight text-gray-900">
              One marketplace,{' '}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: `linear-gradient(to right, #ffb74d 0%, ${BRAND} 55%, #e8720c 100%)`,
                }}
              >
                every vendor
              </span>{' '}
              you need.
            </h1>

            <p className="text-gray-600 text-lg leading-relaxed max-w-md">
              Dehleze connects vendors and customers on one platform — built
              for sellers to reach more people, and for buyers to find what
              they need, delivered reliably.
            </p>

            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                to="/"
                className="rounded-full px-6 py-3 text-sm font-semibold text-white shadow-lg hover:scale-[1.03] transition-transform"
                style={{
                  backgroundImage: `linear-gradient(to right, #ffb74d, ${BRAND})`,
                  boxShadow: '0 10px 25px -8px rgba(253,158,16,0.45)',
                }}
              >
                Back to home
              </Link>
              <Link
                to="/contact"
                className="rounded-full px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:border-[#fd9e10] hover:text-[#c9660a] transition-colors"
              >
                Get in touch
              </Link>
            </div>
          </div>

          {/* Hero image — signature framed treatment */}
          <div className="relative">
            <div
              className="absolute -inset-4 rounded-[40px] opacity-30 blur-2xl"
              style={{ backgroundImage: `linear-gradient(135deg, #ffb74d, ${BRAND}, #e8720c)` }}
            />
            <div
              className="relative rounded-[32px] p-[3px] shadow-2xl rotate-1 hover:rotate-0 transition-transform duration-500"
              style={{ backgroundImage: `linear-gradient(135deg, #ffb74d, ${BRAND}, #e8720c)` }}
            >
              <div className="overflow-hidden rounded-[29px] bg-white">
                <img
                  src={`/${encodeURIComponent(heroImage)}`}
                  alt="Delivery team"
                  className="w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values strip */}
        <section className="grid sm:grid-cols-3 gap-5">
          {[
            {
              title: 'For Vendors',
              desc: 'List your products and reach customers actively looking to buy.',
              gradient: 'linear-gradient(to right, #ffd08a, #fd9e10)',
            },
            {
              title: 'For Customers',
              desc: 'Browse trusted vendors in one place, with a smooth checkout.',
              gradient: `linear-gradient(to right, ${BRAND}, #f2830c)`,
            },
            {
              title: 'Reliable Delivery',
              desc: 'Every order tracked from vendor to your door.',
              gradient: 'linear-gradient(to right, #f2830c, #e8720c)',
            },
          ].map((v) => (
            <div
              key={v.title}
              className="group relative rounded-3xl bg-white border border-[#ffe3b3] shadow-md hover:shadow-xl transition-shadow p-6 overflow-hidden"
            >
              <div
                className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-20 group-hover:opacity-30 transition-opacity blur-xl"
                style={{ backgroundImage: v.gradient }}
              />
              <div
                className="relative w-10 h-1 rounded-full mb-4"
                style={{ backgroundImage: v.gradient }}
              />
              <h3 className="relative text-lg font-bold text-gray-900 mb-1.5">
                {v.title}
              </h3>
              <p className="relative text-sm text-gray-600 leading-relaxed">
                {v.desc}
              </p>
            </div>
          ))}
        </section>

        {/* Mission / Purchase image section */}
        <section className="grid gap-6 lg:grid-cols-2">
          {[
            {
              title: 'Our Mission',
              image: 'about 100%.PNG',
              alt: 'Our mission image',
            },
            {
              title: 'Our Purchase',
              image: 'bag sale.png',
              alt: 'Our purchase image',
            },
          ].map((card) => (
            <div key={card.title} className="group overflow-hidden rounded-[32px] bg-white shadow-xl border border-gray-200">
              <div className="overflow-hidden transform-gpu transition duration-500 group-hover:scale-105">
                <img
                  src={`/${encodeURIComponent(card.image)}`}
                  alt={card.alt}
                  className="w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900">{card.title}</h3>
              </div>
            </div>
          ))}
        </section>

        {/* Team section */}
        <section className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <div
              className="absolute -inset-4 rounded-[40px] opacity-25 blur-2xl"
              style={{ backgroundImage: `linear-gradient(135deg, #e8720c, ${BRAND}, #ffd08a)` }}
            />
            <div
              className="relative rounded-[32px] p-[3px] shadow-2xl -rotate-1 hover:rotate-0 transition-transform duration-500"
              style={{ backgroundImage: `linear-gradient(135deg, #e8720c, ${BRAND}, #ffd08a)` }}
            >
              <div className="overflow-hidden rounded-[29px] bg-white">
                <img
                  src={`/${encodeURIComponent(teamImage)}`}
                  alt="Office team"
                  className="w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <div className="space-y-5 order-1 lg:order-2">
            <span
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-xs font-semibold tracking-wide border shadow-sm"
              style={{ color: '#c9660a', borderColor: '#ffd9a0' }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: BRAND }} />
              THE TEAM
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 leading-tight">
              The people behind{' '}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(to right, ${BRAND}, #e8720c)` }}
              >
                Dehleze
              </span>
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed max-w-md">
              From onboarding new vendors to supporting every order, this is
              the team keeping the marketplace running smoothly, day in and
              day out.
            </p>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="relative rounded-[32px] overflow-hidden">
          <div
            className="absolute inset-0"
            style={{ backgroundImage: `linear-gradient(to right, #ffb74d, ${BRAND}, #e8720c)` }}
          />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.25),transparent_60%)]" />
          <div className="relative px-8 py-14 text-center space-y-5">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Ready to get started?
            </h2>
            <p className="text-white/90 max-w-md mx-auto">
              Whether you're browsing or selling, Dehleze makes it easy. Reach
              out if you have any questions.
            </p>
            <Link
              to="/contact"
              className="inline-block rounded-full px-7 py-3 text-sm font-semibold bg-white hover:scale-[1.04] transition-transform shadow-lg"
              style={{ color: '#c9660a' }}
            >
              Contact us
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}